import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { saveCashLog, getCashLogs } from '../lib/supabase'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'

const EXPENSE_TYPES = [
  { value: 'payable',   label: 'Supplier Invoice Payment', icon: '🧾', desc: 'Paying an outstanding supplier invoice — links to payables, reduces what is owed' },
  { value: 'invoice',   label: 'Direct Bill Payment',  icon: '📄', desc: 'Paying a bill with no prior payable entry — electricity (postpaid), water, rent, internet' },
  { value: 'direct',    label: 'Direct Purchase',   icon: '⚡', desc: 'Cash purchase with no PO or bill — prepaid electricity tokens, fuel, market purchases' },
  { value: 'petty',     label: 'Petty Cash',        icon: '🪙', desc: 'Small miscellaneous expenses — transport, small repairs, sundries' },
  { value: 'payroll',   label: 'Payroll',           icon: '👷', desc: 'Staff wages and salaries' },
  { value: 'loan',      label: 'Loan / Finance',    icon: '🏦', desc: 'Loan repayments, bank charges, interest payments' },
]

const emptyIncome = () => ({ invoice_id:'', invoice_number:'', customer:'', total:'', payment_method:'Bank Transfer' })
const emptyExpense = () => ({ type:'payable', payable_id:'', supplier_invoice_ref:'', item:'', supplier:'', amount:'', has_receipt:'Yes', notes:'', receipt_url:'', provision_id:'' })

export default function CashLog() {
  const { user } = useAuth()
  const [tab, setTab] = useState('new')
  const [logs, setLogs] = useState([])
  const [detailLog, setDetailLog] = useState(null)
  const [detailJournals, setDetailJournals] = useState([])
  const [coaMap, setCoaMap] = useState({})
  const [openInvoices, setOpenInvoices] = useState([])
  const [openPayables, setOpenPayables] = useState([])
  const [openProvisions, setOpenProvisions] = useState([])
  const [activeStaff, setActiveStaff] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const [uploadingReceipt, setUploadingReceipt] = useState(null)

  const [form, setForm] = useState({
    log_date: ugandaDate(),
    opening_cash: '',
    closing_cash: '',
    bank_balance: '',
    notes: '',
    income_rows: [emptyIncome()],
    expense_rows: [emptyExpense()],
  })

  useEffect(() => { loadReferenceData() }, [])
  useEffect(() => { if (tab==='history') loadLogs() }, [tab])

  const loadReferenceData = async () => {
    const [invRes, payableRes, provRes, staffRes] = await Promise.all([
      supabase.from('invoices').select('id,invoice_number,customer_name,balance_due').in('status',['Unpaid','Partial']).order('invoice_date',{ascending:false}),
      supabase.from('payables').select('id,supplier_name,supplier,invoice_ref,description,amount_due,amount,due_date').eq('status','Unpaid').order('created_at',{ascending:false}).limit(100),
      supabase.from('accrual_provisions').select('id,category,description,estimated_amount,period_month').eq('is_reconciled',false).order('period_month',{ascending:false}),
      supabase.from('staff_register').select('id,full_name,salary_amount,salary_frequency,department').eq('is_active',true).order('full_name'),
    ])
    setOpenInvoices(invRes.data||[])
    setOpenPayables(payableRes.data||[])
    setOpenProvisions(provRes.data||[])
    setActiveStaff(staffRes.data||[])
  }

  const loadLogs = async () => {
    setLogsLoading(true)
    const { data } = await getCashLogs({ limit: 50 })
    setLogs(data||[])
    setLogsLoading(false)
  }

  const openDetail = async (log) => {
    setDetailLog(log)
    // Load COA map for account name lookup if not already loaded
    if (Object.keys(coaMap).length === 0) {
      const { data: coa } = await supabase.from('chart_of_accounts').select('code,name')
      const map = {}
      ;(coa || []).forEach(a => { map[a.code] = a.name })
      setCoaMap(map)
    }
    const { data: journals } = await supabase
      .from('journal_entries')
      .select('*')
      .eq('entry_date', log.log_date)
      .in('module', ['cash_expense', 'customer_payment', 'payables', 'loan_repayment', 'interest_payment'])
      .order('created_at')
    setDetailJournals(journals || [])
  }

  const handleReceiptUpload = async (rowIndex, file) => {
    if (!file) return
    setUploadingReceipt(rowIndex)
    const ext = file.name.split('.').pop()
    const path = `receipts/${Date.now()}_${rowIndex}.${ext}`
    const { error: uploadErr } = await supabase.storage.from('receipts').upload(path, file)
    if (uploadErr) { setError('Receipt upload failed: ' + uploadErr.message); setUploadingReceipt(null); return }
    const { data: { publicUrl } } = supabase.storage.from('receipts').getPublicUrl(path)
    const rows = [...form.expense_rows]
    rows[rowIndex] = { ...rows[rowIndex], receipt_url: publicUrl, has_receipt: 'Yes' }
    setForm(f => ({ ...f, expense_rows: rows }))
    setUploadingReceipt(null)
  }

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }))

  const setIncomeRow = (i, field, val) => {
    const rows = [...form.income_rows]
    rows[i] = { ...rows[i], [field]: val }
    if (field==='invoice_id' && val) {
      const inv = openInvoices.find(inv=>inv.id===val)
      if (inv) { rows[i].invoice_number = inv.invoice_number; rows[i].customer = inv.customer_name; rows[i].total = inv.balance_due?.toString()||'' }
    }
    setForm(f=>({...f,income_rows:rows}))
  }

  const setExpenseRow = (i, field, val) => {
    const rows = [...form.expense_rows]
    rows[i] = { ...rows[i], [field]: val }

    if (field==='provision_id' && val) {
      const prov = openProvisions.find(p=>p.id===val)
      if (prov) { rows[i].item = prov.category + (prov.description ? ' — '+prov.description : '') }
    }
    setForm(f=>({...f,expense_rows:rows}))
  }

  const totalIncome = form.income_rows.reduce((s,r)=>s+(parseFloat(r.total)||0),0)
  const totalExpenses = form.expense_rows.reduce((s,r)=>s+(parseFloat(r.amount)||0),0)
  const netToday = totalIncome - totalExpenses

  const needsReceipt = (type) => ['direct','petty'].includes(type)
  const needsPO = (type) => type === 'po'
  const needsInvoiceRef = (type) => type === 'invoice'

  const handleSave = async () => {
    // Validate income
    const incomeErrors = form.income_rows.filter(r=>r.total&&!r.invoice_id)
    if (incomeErrors.length>0) { setError('All income entries must be linked to an invoice. Create the invoice first if needed.'); return }

    // Validate expenses
    const payableErrors = form.expense_rows.filter(r=>r.amount&&r.type==='payable'&&!r.payable_id)
    if (payableErrors.length>0) { setError('Supplier Invoice Payment must be linked to an outstanding payable.'); return }

    // ⚠️ NEGATIVE CASH WARNING — check if this entry would make cash go negative
    if (totalExpenses > totalIncome) {
      const { data: allLogs } = await supabase.from('cash_logs').select('total_income, total_expenses')
      const currentCash = (allLogs||[]).reduce((s,l) => s + (parseFloat(l.total_income)||0) - (parseFloat(l.total_expenses)||0), 0)
      const projectedCash = currentCash + netToday
      if (projectedCash < 0) {
        setError(`⚠️ CASH ALERT: This entry would make cash go negative (UGX ${Math.round(projectedCash).toLocaleString()}). Current balance: UGX ${Math.round(currentCash).toLocaleString()}. Please verify the amounts before saving. Edward and Sharon have been alerted.`)
        // Send email alert
        try {
          await fetch('/api/notify-cash-negative', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              current_cash: currentCash,
              projected_cash: projectedCash,
              expense_amount: totalExpenses,
              log_date: form.log_date,
            })
          })
        } catch(e) {}
        setSaving(false)
        return
      }
    }

    setSaving(true); setError(''); setSaved(false)
    const payload = { ...form, user_id:user.id, total_income:totalIncome, total_expenses:totalExpenses, net:netToday }
    const { error:err } = await saveCashLog(payload)
    if (err) { setError(err.message); setSaving(false); return }

    // ── POST JOURNAL ENTRIES ─────────────────────────────────────────────
    const jDate = form.log_date
    const jTs = Date.now()

    // Expense rows → Dr expense account / Cr 1101 Cash
    const expenseAccountMap = {
      'Seeds & Seedlings': '5100',
      'Nutrients & Chemicals': '5200',
      'Growing Media': '5300',
      'Packaging Materials': '5400',
      'Direct Labour': '5500',
      'Management Salaries': '6100',
      'Electricity': '6200', 'Electricity - General': '6200', 'Electricity - Growing': '6200',
      'Fuel & Vehicle': '6300', 'Fuel': '6300',
      'Equipment Repairs & Maintenance': '6400', 'Repairs': '6400',
      'Rent': '6500',
      'Insurance': '6600',
      'Marketing & Delivery': '6700', 'Marketing': '6700',
      'Professional Fees': '6800',
      'Bank Charges': '6900',
      'Interest Expense': '8100',
      'Loan Repayment': '2201',
    }
    for (let i = 0; i < form.expense_rows.length; i++) {
      const row = form.expense_rows[i]
      const amt = parseFloat(row.amount)
      if (!amt || amt <= 0) continue
      const category = row.category || row.item || row.type || 'Miscellaneous'
      const expenseAccount = expenseAccountMap[category] || '9000'
      await supabase.from('journal_entries').insert({
        journal_id: `JNL-EXP-${jTs}-${i}`,
        entry_date: jDate,
        module: 'cash_expense',
        reference: row.supplier_invoice_ref || row.payable_id || null,
        debit_account: expenseAccount,
        credit_account: '1101',
        amount: amt,
        description: row.item || row.description || category,
        created_by: user.id,
      })
    }

    // Income rows → Dr 1101 Cash / Cr 1201 Trade Receivables (clears the receivable)
    for (let i = 0; i < form.income_rows.length; i++) {
      const row = form.income_rows[i]
      const amt = parseFloat(row.total)
      if (!amt || amt <= 0) continue
      await supabase.from('journal_entries').insert({
        journal_id: `JNL-RCPT-${jTs}-${i}`,
        entry_date: jDate,
        module: 'customer_payment',
        reference: row.invoice_number || null,
        debit_account: '1101',
        credit_account: '1201',
        amount: amt,
        description: `Payment received — ${row.customer || row.invoice_number || ''}`,
        source_id: row.invoice_id || null,
        created_by: user.id,
      })
    }

    // Auto-reconcile linked provisions
    for (const row of form.expense_rows) {
      if (row.provision_id && row.amount) {
        await supabase.from('accrual_provisions').update({
          actual_amount: parseFloat(row.amount),
          is_reconciled: true,
          reconciled_at: new Date().toISOString(),
          reconciled_by: user.id,
        }).eq('id', row.provision_id)
      }
    }

    // Mark invoices as paid/partial
    for (const row of form.income_rows) {
      if (row.invoice_id && row.total) {
        const inv = openInvoices.find(i=>i.id===row.invoice_id)
        if (inv) {
          const paid = parseFloat(row.total)||0
          const newBalance = (inv.balance_due||0) - paid
          await supabase.from('invoices').update({
            balance_due: Math.max(0,newBalance),
            amount_paid: (inv.amount||0) - Math.max(0,newBalance),
            status: newBalance<=0 ? 'Paid' : 'Partial'
          }).eq('id', row.invoice_id)
        }
      }
    }

    setSaved(true); setSaving(false)
    setTimeout(()=>setSaved(false),3000)
    loadReferenceData()
  }

  const typeInfo = (type) => EXPENSE_TYPES.find(t=>t.value===type)||EXPENSE_TYPES[0]

  return (
    <AppLayout title="Daily Cash Log">
      <div className="section-header">
        <div className="section-icon">💵</div>
        <div className="section-info">
          <h2>Daily Cash Log</h2>
          <p>All income must be linked to an invoice. Record every expense with the correct type.</p>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${tab==='new'?'active':''}`} onClick={()=>setTab('new')}>📝 Today's Log</button>
        <button className={`tab ${tab==='history'?'active':''}`} onClick={()=>setTab('history')}>📚 History</button>
      </div>

      {tab==='new' && (
        <div className="card">
          {saved && <div className="alert alert-green">✅ Cash log saved!{form.expense_rows.some(r=>r.provision_id) ? ' Linked provisions have been auto-reconciled.' : ''}</div>}
          {error && <div className="alert alert-red">❌ {error}</div>}

          <div className="grid-2" style={{marginBottom:16}}>
            <div className="form-group"><label className="form-label">Date</label><input className="form-input" type="date" value={form.log_date} onChange={e=>set('log_date',e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Opening Petty Cash (UGX)</label><input className="form-input" type="number" placeholder="0" value={form.opening_cash} onChange={e=>set('opening_cash',e.target.value)} /></div>
          </div>

          {/* ── INCOME ── */}
          <hr className="divider"/>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
            <div style={{fontWeight:700}}>💵 Cash Received — Customer Payments</div>
            <div style={{fontSize:'0.85rem',fontWeight:700,color:'var(--green-mid)'}}>Total: UGX {totalIncome.toLocaleString()}</div>
          </div>
          <div className="alert alert-amber" style={{marginBottom:10,fontSize:'0.78rem',padding:'8px 12px'}}>
            ⚠️ Only customer invoice payments here. Loan receipts and equity injections are recorded in their respective pages and flow to cash automatically.
          </div>
          <div className="table-wrap" style={{marginBottom:8}}>
            <table>
              <thead><tr><th>Invoice</th><th>Customer</th><th>Amount Paid (UGX)</th><th>Method</th><th style={{width:32}}></th></tr></thead>
              <tbody>
                {form.income_rows.map((row,i)=>(
                  <tr key={i}>
                    <td>
                      <select className="form-select" value={row.invoice_id} onChange={e=>setIncomeRow(i,'invoice_id',e.target.value)}>
                        <option value="">— Select invoice —</option>
                        {openInvoices.map(inv=><option key={inv.id} value={inv.id}>{inv.invoice_number} — {inv.customer_name} (UGX {(inv.balance_due||0).toLocaleString()} due)</option>)}
                      </select>
                    </td>
                    <td><input className="form-input" placeholder="Auto-filled" value={row.customer} onChange={e=>setIncomeRow(i,'customer',e.target.value)} /></td>
                    <td><input className="form-input" type="number" style={{width:130}} placeholder="0" value={row.total} onChange={e=>setIncomeRow(i,'total',e.target.value)} /></td>
                    <td>
                      <select className="form-select" style={{width:130}} value={row.payment_method} onChange={e=>setIncomeRow(i,'payment_method',e.target.value)}>
                        {['Bank Transfer','Cash','Mobile Money','Card'].map(o=><option key={o}>{o}</option>)}
                      </select>
                    </td>
                    <td>{form.income_rows.length>1&&<button className="btn-del-row" onClick={()=>setForm(f=>({...f,income_rows:f.income_rows.filter((_,j)=>j!==i)}))}>✕</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={()=>setForm(f=>({...f,income_rows:[...f.income_rows,emptyIncome()]}))}>+ Add Receipt</button>

          {/* ── EXPENSES ── */}
          <hr className="divider"/>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
            <div style={{fontWeight:700}}>💸 Cash Expenses</div>
            <div style={{fontSize:'0.85rem',fontWeight:700,color:'var(--red)'}}>Total: UGX {totalExpenses.toLocaleString()}</div>
          </div>

          {/* Expense type legend */}
          <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:12}}>
            {EXPENSE_TYPES.map(t=>(
              <span key={t.value} style={{fontSize:'0.68rem',background:'var(--cream)',border:'1px solid var(--border)',borderRadius:6,padding:'3px 8px',color:'var(--text-muted)'}}>
                {t.icon} <strong>{t.label}</strong> — {t.desc}
              </span>
            ))}
          </div>

          <div className="table-wrap" style={{marginBottom:8}}>
            <table>
              <thead>
                <tr><th>Type</th><th>Reference / Detail</th><th>Supplier</th><th>Item / Description</th><th>Amount (UGX)</th><th>Receipt</th><th style={{width:32}}></th></tr>
              </thead>
              <tbody>
                {form.expense_rows.map((row,i)=>{
                  const t = typeInfo(row.type)
                  return (
                    <tr key={i} style={{background: row.provision_id ? 'var(--amber-pale)' : undefined}}>
                      <td>
                        <select className="form-select" style={{width:130}} value={row.type} onChange={e=>setExpenseRow(i,'type',e.target.value)}>
                          {EXPENSE_TYPES.map(t=><option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
                        </select>
                      </td>
                      <td>
                        {row.type==='payable' && (
                          <div>
                            <select className="form-select" value={row.payable_id} onChange={e => {
                              const p = openPayables?.find(p=>p.id===e.target.value)
                              setExpenseRow(i,'payable_id',e.target.value)
                              if (p) {
                                setExpenseRow(i,'supplier',p.supplier_name||p.supplier||'')
                                setExpenseRow(i,'item',p.description||p.invoice_ref||'')
                                setExpenseRow(i,'amount',p.amount_due?.toString()||p.amount?.toString()||'')
                              }
                            }}>
                              <option value="">— Select outstanding invoice —</option>
                              {(openPayables||[]).map(p=><option key={p.id} value={p.id}>{p.supplier_name||p.supplier} — {p.invoice_ref||p.description} (UGX {parseFloat(p.amount_due||p.amount||0).toLocaleString()})</option>)}
                            </select>
                            {(openPayables||[]).length===0 && <div style={{fontSize:'0.72rem',color:'var(--amber)',marginTop:4}}>No outstanding payables. Record supplier invoices in Payables first.</div>}
                          </div>
                        )}
                        {row.type==='invoice' && (
                          <input className="form-input" placeholder="Invoice/bill ref e.g. UMEME-2026-04" value={row.supplier_invoice_ref} onChange={e=>setExpenseRow(i,'supplier_invoice_ref',e.target.value)} />
                        )}
                        {row.type==='direct' && (
                          <div>
                            <input className="form-input" placeholder="e.g. Electricity tokens, Fuel" value={row.item} onChange={e=>setExpenseRow(i,'item',e.target.value)} />
                            {openProvisions.length>0 && (
                              <select className="form-select" style={{marginTop:4}} value={row.provision_id} onChange={e=>setExpenseRow(i,'provision_id',e.target.value)}>
                                <option value="">— Link to provision (optional) —</option>
                                {openProvisions.map(p=><option key={p.id} value={p.id}>📌 {p.category}{p.description?' — '+p.description:''} (est. UGX {parseFloat(p.estimated_amount).toLocaleString()})</option>)}
                              </select>
                            )}
                          </div>
                        )}
                        {row.type==='petty' && (
                          <input className="form-input" placeholder="Reason / description" value={row.notes} onChange={e=>setExpenseRow(i,'notes',e.target.value)} />
                        )}
                        {row.type==='payroll' && (
                          <div>
                            <select className="form-select" value={row.notes} onChange={e => {
                              const s = activeStaff.find(s=>s.full_name===e.target.value)
                              setExpenseRow(i,'notes',e.target.value)
                              if (s) {
                                setExpenseRow(i,'supplier',s.full_name)
                                if (s.salary_amount && !form.expense_rows[i].amount) setExpenseRow(i,'amount',s.salary_amount.toString())
                              }
                            }}>
                              <option value="">— Select staff member —</option>
                              {activeStaff.map(s=><option key={s.id} value={s.full_name}>{s.full_name} {s.department?`(${s.department})`:''} {s.salary_amount?`— UGX ${parseFloat(s.salary_amount).toLocaleString()}`:''}</option>)}
                            </select>
                            <input className="form-input" style={{marginTop:4}} placeholder="Payroll period e.g. April 2026" value={row.supplier_invoice_ref} onChange={e=>setExpenseRow(i,'supplier_invoice_ref',e.target.value)} />
                            {activeStaff.length===0 && <div style={{fontSize:'0.72rem',color:'var(--red)',marginTop:4}}>⚠️ No staff in register. Add staff in Staff Register first.</div>}
                          </div>
                        )}
                        {row.type==='loan' && (
                          <input className="form-input" placeholder="e.g. Loan ref, bank charge ref" value={row.supplier_invoice_ref} onChange={e=>setExpenseRow(i,'supplier_invoice_ref',e.target.value)} />
                        )}
                        {row.provision_id && (
                          <div style={{fontSize:'0.68rem',color:'var(--amber)',fontWeight:700,marginTop:3}}>📌 Will auto-reconcile provision on save</div>
                        )}
                      </td>
                      <td><input className="form-input" style={{width:120}} placeholder="Supplier name" value={row.supplier} onChange={e=>setExpenseRow(i,'supplier',e.target.value)} /></td>
                      <td>
                        {!['direct','petty','payroll','loan'].includes(row.type) && (
                          <input className="form-input" placeholder="Item description" value={row.item} onChange={e=>setExpenseRow(i,'item',e.target.value)} />
                        )}
                      </td>
                      <td><input className="form-input" style={{width:110}} type="number" placeholder="0" value={row.amount} onChange={e=>setExpenseRow(i,'amount',e.target.value)} /></td>
                      <td>
                        <div style={{display:'flex',flexDirection:'column',gap:4}}>
                          <select className="form-select" style={{width:70}} value={row.has_receipt} onChange={e=>setExpenseRow(i,'has_receipt',e.target.value)}>
                            <option>Yes</option><option>No</option>
                          </select>
                          {needsReceipt(row.type) && (
                            <div style={{display:'flex',gap:4}}>
                              {row.receipt_url
                                ? <a href={row.receipt_url} target="_blank" rel="noreferrer" style={{fontSize:'0.7rem',color:'var(--green-mid)',fontWeight:700}}>📎 View</a>
                                : <>
                                    <button className="btn btn-secondary btn-sm" style={{fontSize:'0.65rem',padding:'2px 6px'}} onClick={()=>{const el=document.createElement('input');el.type='file';el.accept='image/*';el.capture='environment';el.onchange=e=>handleReceiptUpload(i,e.target.files[0]);el.click()}}>📷</button>
                                    <button className="btn btn-secondary btn-sm" style={{fontSize:'0.65rem',padding:'2px 6px'}} onClick={()=>{const el=document.createElement('input');el.type='file';el.accept='image/*,application/pdf';el.onchange=e=>handleReceiptUpload(i,e.target.files[0]);el.click()}}>📁</button>
                                    {uploadingReceipt===i&&<span className="spinner dark" style={{width:14,height:14}}/>}
                                  </>
                              }
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{form.expense_rows.length>1&&<button className="btn-del-row" onClick={()=>setForm(f=>({...f,expense_rows:f.expense_rows.filter((_,j)=>j!==i)}))}>✕</button>}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={()=>setForm(f=>({...f,expense_rows:[...f.expense_rows,emptyExpense()]}))}>+ Add Expense</button>

          <hr className="divider"/>
          <div style={{background:netToday>=0?'var(--green-pale)':'var(--red-pale)',borderRadius:10,padding:16,marginBottom:16}}>
            <div className="grid-3">
              {[['Total Income','UGX '+totalIncome.toLocaleString(),'var(--green-mid)'],['Total Expenses','UGX '+totalExpenses.toLocaleString(),'var(--red)'],['Net Today',(netToday>=0?'+':'')+'UGX '+netToday.toLocaleString(),netToday>=0?'var(--green-mid)':'var(--red)']].map(([label,val,color])=>(
                <div key={label}>
                  <div style={{fontSize:'0.72rem',fontFamily:"'DM Mono',monospace",textTransform:'uppercase',letterSpacing:'0.1em',color:'var(--text-muted)',marginBottom:4}}>{label}</div>
                  <div style={{fontSize:'1.2rem',fontWeight:700,color,fontFamily:"'Cormorant Garamond',serif"}}>{val}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group"><label className="form-label">Closing Petty Cash (UGX)</label><input className="form-input" type="number" placeholder="0" value={form.closing_cash} onChange={e=>set('closing_cash',e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Cash in Bank (end of day)</label><input className="form-input" type="number" placeholder="0" value={form.bank_balance} onChange={e=>set('bank_balance',e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" style={{minHeight:60}} value={form.notes} onChange={e=>set('notes',e.target.value)} /></div>

          <div style={{display:'flex',gap:10}}>
            <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>
              {saving?<><span className="spinner"/>Saving...</>:'💾 Save to Cloud'}
            </button>
            <button className="btn btn-ghost" onClick={()=>window.print()}>🖨️ Print</button>
          </div>
        </div>
      )}

      {tab==='history' && (
        <div className="card">
          <div className="card-title">Cash Log History</div>
          {logsLoading?(
            <div style={{textAlign:'center',padding:32}}><span className="spinner dark"/></div>
          ):logs.length===0?(
            <div className="empty-state"><div className="empty-icon">💵</div><h3>No cash logs yet</h3></div>
          ):(
            <div className="table-wrap">
              <table>
                <thead><tr><th>Date</th><th>Description</th><th>Logged By</th><th>Cash In (UGX)</th><th>Cash Out (UGX)</th><th>Net (UGX)</th><th>Balance (UGX)</th></tr></thead>
                <tbody>
                  {(() => {
                    // Calculate running balance oldest-first, display newest-first
                    const sorted = [...logs].sort((a,b) => new Date(a.log_date) - new Date(b.log_date))
                    let running = 0
                    const withBalance = sorted.map(log => {
                      running += (parseFloat(log.total_income)||0) - (parseFloat(log.total_expenses)||0)
                      return { ...log, runningBalance: running }
                    })
                    // Display newest first
                    return withBalance.reverse().map(log => {
                      const incomeDesc = (log.income_rows||[]).filter(r=>r.total>0||r.description).map(r=>r.description||r.customer||r.invoice_number||'').filter(Boolean).join(', ')
                      const expenseDesc = (log.expense_rows||[]).filter(r=>r.amount>0||r.description||r.item).map(r=>r.description||r.item||r.supplier||'').filter(Boolean).join(', ')
                      const desc = log.notes || incomeDesc || expenseDesc || '—'
                      return (
                        <tr key={log.id} 
                          onClick={() => openDetail(log)}
                          style={{ background: log.runningBalance < 0 ? 'var(--red-pale)' : 'transparent', cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background = log.runningBalance < 0 ? 'var(--red-pale)' : 'var(--cream)'}
                          onMouseLeave={e => e.currentTarget.style.background = log.runningBalance < 0 ? 'var(--red-pale)' : 'transparent'}>
                          <td style={{fontWeight:600, whiteSpace:'nowrap'}}>{new Date(log.log_date).toLocaleDateString('en-UG')}</td>
                          <td style={{fontSize:'0.78rem', color:'var(--text-muted)', maxWidth:220}}>{desc.length > 60 ? desc.slice(0,60)+'...' : desc}</td>
                          <td style={{fontSize:'0.75rem'}}>{log.profiles?.full_name||'System'}</td>
                          <td style={{color:'var(--green-mid)',fontWeight:600}}>{log.total_income>0?log.total_income.toLocaleString():'—'}</td>
                          <td style={{color:'var(--red)'}}>{log.total_expenses>0?log.total_expenses.toLocaleString():'—'}</td>
                          <td><span style={{fontWeight:700,color:(log.net||0)>=0?'var(--green-mid)':'var(--red)'}}>{(log.net||0)>=0?'+':''}{(log.net||0).toLocaleString()}</span></td>
                          <td style={{fontWeight:700, color: log.runningBalance < 0 ? 'var(--red)' : 'var(--green-deep)', fontFamily:"'DM Mono',monospace"}}>
                            {log.runningBalance < 0 ? '⚠️ ' : ''}{Math.round(log.runningBalance).toLocaleString()}
                          </td>
                        </tr>
                      )
                    })
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {/* CASH LOG DETAIL MODAL */}
      {detailLog && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDetailLog(null)}>
          <div className="modal" style={{ maxWidth: 640, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <div className="modal-title">💵 Cash Log Detail</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  {new Date(detailLog.log_date).toLocaleDateString('en-UG', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}
                  {detailLog.created_at && ` · Logged at ${new Date(detailLog.created_at).toLocaleTimeString('en-UG', { hour:'2-digit', minute:'2-digit' })}`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => {
                  const w = window.open('', '_blank')
                  const incRows = (detailLog.income_rows||[]).filter(r => r.total > 0)
                  const expRows = (detailLog.expense_rows||[]).filter(r => r.amount > 0)
                  w.document.write(`
                    <html><head><title>Cash Log ${detailLog.log_date}</title>
                    <style>
                      body{font-family:Arial,sans-serif;padding:32px;max-width:700px;margin:0 auto;color:#1C3520;}
                      h2{font-size:1.2rem;margin-bottom:4px;}
                      table{width:100%;border-collapse:collapse;margin:12px 0;}
                      th{background:#f0ede8;padding:8px;text-align:left;font-size:0.8rem;}
                      td{padding:8px;border-bottom:1px solid #eee;font-size:0.85rem;}
                      .total{font-weight:700;font-size:1rem;}
                      .section{margin-top:16px;font-weight:700;font-size:0.9rem;color:#1C3520;border-bottom:2px solid #1C3520;padding-bottom:4px;}
                      .footer{margin-top:32px;font-size:0.72rem;color:#888;}
                    </style></head><body>
                    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
                      <div><h2>Cash Log — ${new Date(detailLog.log_date).toLocaleDateString('en-UG',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</h2>
                      <div style="color:#666;font-size:0.8rem;">Logged by: ${detailLog.profiles?.full_name||'System'} · ${detailLog.created_at ? new Date(detailLog.created_at).toLocaleString('en-UG') : ''}</div></div>
                      <div style="font-size:0.75rem;color:#666;text-align:right;">Kobflo Greentek Ltd</div>
                    </div>
                    ${incRows.length > 0 ? `
                      <div class="section">CASH IN</div>
                      <table><thead><tr><th>Invoice / Reference</th><th>Customer / Description</th><th>Method</th><th>Amount (UGX)</th></tr></thead>
                      <tbody>${incRows.map(r=>`<tr><td>${r.invoice_number||'—'}</td><td>${r.customer||r.description||'—'}</td><td>${r.payment_method||'—'}</td><td style="font-weight:600;">${parseFloat(r.total||0).toLocaleString()}</td></tr>`).join('')}</tbody>
                      </table>` : ''}
                    ${expRows.length > 0 ? `
                      <div class="section">CASH OUT</div>
                      <table><thead><tr><th>Type</th><th>Description</th><th>Supplier / Ref</th><th>Amount (UGX)</th></tr></thead>
                      <tbody>${expRows.map(r=>`<tr><td>${r.type||'—'}</td><td>${r.item||r.description||'—'}</td><td>${r.supplier||r.supplier_invoice_ref||'—'}</td><td style="font-weight:600;">${parseFloat(r.amount||0).toLocaleString()}</td></tr>`).join('')}</tbody>
                      </table>` : ''}
                    <div style="display:flex;justify-content:flex-end;gap:24px;margin-top:16px;padding:12px;background:#f9f5ec;border-radius:6px;">
                      <div class="total">Cash In: UGX ${(detailLog.total_income||0).toLocaleString()}</div>
                      <div class="total">Cash Out: UGX ${(detailLog.total_expenses||0).toLocaleString()}</div>
                      <div class="total" style="color:${(detailLog.net||0)>=0?'#16a34a':'#dc2626'}">Net: ${(detailLog.net||0)>=0?'+':''}UGX ${(detailLog.net||0).toLocaleString()}</div>
                    </div>
                    ${detailLog.notes ? `<p style="margin-top:12px;font-size:0.85rem;"><strong>Notes:</strong> ${detailLog.notes}</p>` : ''}
                    <div class="footer">Kobflo Greentek Ltd · app.kobfloagri.com · Generated ${new Date().toLocaleString('en-UG')}</div>
                    </body></html>`)
                  w.print()
                }}>🖨️ Print</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setDetailLog(null)}>✕ Close</button>
              </div>
            </div>

            {/* Meta info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
              {[
                ['Logged by', detailLog.profiles?.full_name || 'System'],
                ['Date', new Date(detailLog.log_date).toLocaleDateString('en-UG', { weekday:'long', day:'numeric', month:'long', year:'numeric' })],
                ['Time stamped', detailLog.created_at ? new Date(detailLog.created_at).toLocaleString('en-UG') : '—'],
                ['Net movement', `${(detailLog.net||0) >= 0 ? '+' : ''}UGX ${(detailLog.net||0).toLocaleString()}`],
              ].map(([label, val]) => (
                <div key={label} style={{ background: 'var(--cream)', borderRadius: 6, padding: '8px 12px' }}>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: "'DM Mono',monospace", marginBottom: 2 }}>{label}</div>
                  <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{val}</div>
                </div>
              ))}
            </div>

            {/* Full notes */}
            {detailLog.notes && (
              <div style={{ background: 'var(--cream)', borderRadius: 6, padding: '10px 12px', marginBottom: 14, fontSize: '0.82rem' }}>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: "'DM Mono',monospace", marginBottom: 4 }}>Notes</div>
                {detailLog.notes}
              </div>
            )}

            {/* Cash In rows */}
            {(detailLog.income_rows||[]).filter(r => r.total > 0).length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--green-mid)', marginBottom: 8 }}>💚 Cash In</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead><tr style={{ background: 'var(--green-pale)' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Invoice</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Customer / Description</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Method</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Amount (UGX)</th>
                  </tr></thead>
                  <tbody>
                    {(detailLog.income_rows||[]).filter(r => r.total > 0).map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 8px', fontFamily: "'DM Mono',monospace", fontSize: '0.75rem' }}>{r.invoice_number || '—'}</td>
                        <td style={{ padding: '6px 8px' }}>{r.customer || r.description || '—'}</td>
                        <td style={{ padding: '6px 8px', color: 'var(--text-muted)', fontSize: '0.75rem' }}>{r.payment_method || '—'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: 'var(--green-mid)' }}>{parseFloat(r.total||0).toLocaleString()}</td>
                      </tr>
                    ))}
                    <tr style={{ background: 'var(--green-pale)', fontWeight: 700 }}>
                      <td colSpan={3} style={{ padding: '6px 8px' }}>Total Cash In</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>UGX {(detailLog.total_income||0).toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Cash Out rows */}
            {(detailLog.expense_rows||[]).filter(r => r.amount > 0).length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--red)', marginBottom: 8 }}>🔴 Cash Out</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead><tr style={{ background: 'var(--red-pale)' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Type</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Description</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Supplier / Ref</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Amount (UGX)</th>
                  </tr></thead>
                  <tbody>
                    {(detailLog.expense_rows||[]).filter(r => r.amount > 0).map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 8px', fontSize: '0.75rem' }}>{r.type || '—'}</td>
                        <td style={{ padding: '6px 8px' }}>{r.item || r.description || '—'}</td>
                        <td style={{ padding: '6px 8px', color: 'var(--text-muted)', fontSize: '0.75rem' }}>{r.supplier || r.supplier_invoice_ref || r.notes || '—'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: 'var(--red)' }}>{parseFloat(r.amount||0).toLocaleString()}</td>
                      </tr>
                    ))}
                    <tr style={{ background: 'var(--red-pale)', fontWeight: 700 }}>
                      <td colSpan={3} style={{ padding: '6px 8px' }}>Total Cash Out</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--red)' }}>UGX {(detailLog.total_expenses||0).toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Journal Entries */}
            {detailJournals.length > 0 && (
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 8 }}>📋 Journal Movements</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                  <thead><tr style={{ background: 'var(--cream)' }}>
                    <th style={{ padding: '5px 8px', textAlign: 'left' }}>Dr Account</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left' }}>Cr Account</th>
                    <th style={{ padding: '5px 8px', textAlign: 'right' }}>Amount (UGX)</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left' }}>Description</th>
                  </tr></thead>
                  <tbody>
                    {detailJournals.map((j, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '5px 8px', fontFamily: "'DM Mono',monospace", color: 'var(--green-deep)', fontSize: '0.75rem' }}>
                          {j.debit_account} — {coaMap[j.debit_account] || j.debit_account}
                        </td>
                        <td style={{ padding: '5px 8px', fontFamily: "'DM Mono',monospace", color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                          {j.credit_account} — {coaMap[j.credit_account] || j.credit_account}
                        </td>
                        <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600 }}>{j.amount.toLocaleString()}</td>
                        <td style={{ padding: '5px 8px', color: 'var(--text-muted)', fontSize: '0.72rem' }}>{j.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

    </AppLayout>
  )
}
